import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IRoom, roomSchema } from '../Interfaces/IRoom';
import { useDeleteRoom, useGetRooms } from '../hooks/rooms';
import { BrandedDataGridOverlay } from '../core/BrandedDataGridOverlay';
import { useAlert } from '../core/AlertContext';
import RoomEdit from './RoomEdit';
import RowActionsMenu from '../core/RowActionsMenu';
import AuditInfoDialog from '../core/AuditInfoDialog';
import DetailScreenHeader from '../core/DetailScreenHeader';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../calendar/dialogLayout';
import PageHeader from '../core/PageHeader';
import PvtaCard from '../core/PvtaCard';
import { primaryButtonSx, secondaryButtonSx, dangerButtonSx } from '../core/pvtaStyles';

const emptyRoom = roomSchema.parse({});

const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return '-';
    }
    return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));
};

const getLastModifiedBy = (room: IRoom): string =>
    room.last_modified_by?.displayName ?? room.created_by?.displayName ?? '-';

const getLastModifiedAt = (room: IRoom): string =>
    formatDateTime(room.last_modified_at ?? room.updated_at ?? room.created_at ?? null);

const RoomOverview: React.FC = () => {
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState<IRoom | null>(null);
    const [deleteCandidate, setDeleteCandidate] = useState<IRoom | null>(null);
    const [infoCandidate, setInfoCandidate] = useState<IRoom | null>(null);
    const { roomArray, isLoadingRoomArray } = useGetRooms({ includeDeleted });
    const { deleteRoom, isDeletingRoom } = useDeleteRoom();
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleDelete = async () => {
        if (!deleteCandidate) {
            return;
        }
        try {
            await deleteRoom(deleteCandidate.id);
            showAlert({ text: 'Zimmer erfolgreich gelöscht.' });
            setDeleteCandidate(null);
        } catch {
            showAlert({ text: 'Fehler beim Löschen des Zimmers.', severity: 'error' });
        }
    };

    const columns: GridColDef<IRoom>[] = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
        ...(!isMobile ? [{
            field: 'description',
            headerName: 'Beschreibung',
            flex: 1.2,
            minWidth: 220,
            valueGetter: (_value: unknown, row: IRoom) => row.description ?? '',
        } as GridColDef<IRoom>] : []),
        {
            field: 'last_modified_by',
            headerName: 'Zuletzt bearbeitet von',
            flex: 0.9,
            minWidth: 190,
            valueGetter: (_value, row) => getLastModifiedBy(row),
        },
        {
            field: 'last_modified_at',
            headerName: 'Zuletzt bearbeitet am',
            flex: 0.9,
            minWidth: 180,
            valueGetter: (_value, row) => getLastModifiedAt(row),
        },
        {
            field: 'deleted_at',
            headerName: 'Gelöscht am',
            flex: 0.8,
            minWidth: 160,
            valueGetter: (_value, row) => formatDateTime(row.deleted_at),
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            sortable: false,
            filterable: false,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<IRoom>) => (
                <RowActionsMenu
                    items={[
                        {
                            label: 'Bearbeiten',
                            icon: <EditOutlinedIcon fontSize="small" />,
                            onClick: () => setSelectedRoom(params.row),
                        },
                        {
                            label: 'Löschen',
                            icon: <DeleteOutlineIcon fontSize="small" />,
                            disabled: Boolean(params.row.deleted_at),
                            onClick: () => setDeleteCandidate(params.row),
                        },
                        {
                            label: 'Weitere Informationen',
                            icon: <InfoOutlinedIcon fontSize="small" />,
                            onClick: () => setInfoCandidate(params.row),
                        },
                    ]}
                />
            ),
        },
    ];

    if (isLoadingRoomArray) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Skeleton variant="text" width={180} height={44} sx={{ mb: 3 }} />
                    <Stack spacing={1}>
                        <Skeleton variant="rounded" height={48} />
                        {[0, 1, 2, 3].map((item) => (
                            <Skeleton key={item} variant="rounded" height={52} />
                        ))}
                    </Stack>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                <PageHeader title="Zimmer" subtitle="Zimmer der Hütte verwalten." />
                <PvtaCard large sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 2, minWidth: 0 }}
                >
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={includeDeleted}
                                onChange={(event) => setIncludeDeleted(event.target.checked)}
                            />
                        )}
                        label="Gelöschte anzeigen"
                    />
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setSelectedRoom(emptyRoom)}
                        sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                    >
                        Neu anlegen
                    </Button>
                </Stack>
                {!isMobile ? (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <DataGrid
                            rows={roomArray}
                            columns={columns}
                            autoHeight
                            disableRowSelectionOnClick
                            onRowClick={(params: GridRowParams<IRoom>) => setSelectedRoom(params.row)}
                            pageSizeOptions={[10, 25, 50]}
                            slots={{
                                noRowsOverlay: BrandedDataGridOverlay,
                                noResultsOverlay: BrandedDataGridOverlay,
                            }}
                            sx={{
                                border: 0,
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                },
                            }}
                        />
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {roomArray.map((room) => (
                            <Card key={room.id} variant="outlined">
                                <CardActionArea onClick={() => setSelectedRoom(room)}>
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {room.name}
                                            </Typography>
                                            {room.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {room.description}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                Zuletzt bearbeitet von {getLastModifiedBy(room)} am {getLastModifiedAt(room)}
                                            </Typography>
                                            {room.deleted_at && (
                                                <Typography variant="caption" color="error.main">
                                                    Gelöscht am {formatDateTime(room.deleted_at)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Stack>
                )}
                </PvtaCard>
                <Dialog
                    open={selectedRoom !== null}
                    onClose={() => setSelectedRoom(null)}
                    maxWidth={EVENT_DIALOG_MAX_WIDTH}
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={EVENT_DIALOG_PAPER_SX}
                >
                    <DetailScreenHeader
                        title={selectedRoom?.id && selectedRoom.id > 0 ? 'Zimmer bearbeiten' : 'Zimmer anlegen'}
                        onClose={() => setSelectedRoom(null)}
                    />
                    <DialogContent sx={{ pt: 2 }}>
                        {selectedRoom && <RoomEdit initialRoom={selectedRoom} onClose={() => setSelectedRoom(null)} showHeading={false} />}
                    </DialogContent>
                </Dialog>
                <Dialog open={deleteCandidate !== null} onClose={() => setDeleteCandidate(null)}>
                    <DialogTitle>Zimmer löschen?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Soll das Zimmer &quot;{deleteCandidate?.name}&quot; wirklich gelöscht werden?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Hinweis: Der Eintrag wird archiviert und kann über den Filter wieder angezeigt werden.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteCandidate(null)} sx={secondaryButtonSx}>Abbrechen</Button>
                        <Button sx={dangerButtonSx} disabled={isDeletingRoom} onClick={() => { void handleDelete(); }}>
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>
                <AuditInfoDialog
                    open={infoCandidate !== null}
                    onClose={() => setInfoCandidate(null)}
                    title="Audit-Informationen"
                    createdBy={infoCandidate?.created_by}
                    createdAt={infoCandidate?.created_at}
                    updatedBy={infoCandidate?.updated_by}
                    updatedAt={infoCandidate?.updated_at}
                    deletedBy={infoCandidate?.deleted_by}
                    deletedAt={infoCandidate?.deleted_at}
                />
            </Container>
        </Box>
    );
};

export default RoomOverview;
